import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatLabel, COUNTRIES } from "@/lib/api";
import { useCoverage } from "@/hooks/usePublicConfig";
import EntryCard from "@/components/EntryCard";
import { ArrowLeft, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Per-jurisdiction (state or country) detail page. Shows total entries,
 * impact mix, category breakdown, and the 50 most-recent entries.
 */
export default function JurisdictionDetail() {
  const { intl } = useCoverage();
  const { name } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/jurisdictions/${encodeURIComponent(name)}`)
      .then((r) => setData(r.data))
      .catch((err) => console.error("Failed to load jurisdiction", err))
      .finally(() => setLoading(false));
  }, [name]);

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-20 font-mono-plex text-sm uppercase tracking-wider text-[#555]">Loading…</div>;
  if (!data || (!intl && (data.level === "international" || COUNTRIES.includes(name)))) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-20" data-testid="jurisdiction-not-found">
        <div className="overline">/not found</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-2">Jurisdiction not found</h1>
        <Link to="/jurisdictions" className="inline-block mt-6 underline font-mono-plex text-sm uppercase tracking-wider">All jurisdictions</Link>
      </div>
    );
  }

  const isCountry = data.level === "international";
  const exportHref = `${process.env.REACT_APP_BACKEND_URL || ''}/api/export/entries?fmt=csv&jurisdiction=${encodeURIComponent(name)}`;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/jurisdictions" className="inline-flex items-center gap-2 font-mono-plex text-sm uppercase tracking-wider hover:underline" data-testid="back-to-jurisdictions">
        <ArrowLeft className="w-4 h-4" /> All jurisdictions
      </Link>

      <div className="mt-4 border-b border-black/15 pb-6 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="overline">/{isCountry ? "country" : "state"}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-2" data-testid="jurisdiction-name">{name}</h1>
          <p className="mt-3 text-base text-[#333]">
            {data.total_entries === 0
              ? "No published entries yet."
              : `${data.total_entries} documented action${data.total_entries === 1 ? "" : "s"} on file.`}
          </p>
        </div>
        {data.total_entries > 0 && (
          <a href={exportHref} data-testid="jurisdiction-export-csv">
            <Button variant="outline" className="rounded-none border-black h-11">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </a>
        )}
      </div>

      {/* Impact breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/15 border border-black/15 mt-8">
        <ImpactTile label="Restricts" value={data.by_impact.restricts} color="#D32F2F" testid="impact-restricts" />
        <ImpactTile label="Protects" value={data.by_impact.protects} color="#008A00" testid="impact-protects" />
        <ImpactTile label="Mixed" value={data.by_impact.mixed} color="#0050FF" testid="impact-mixed" />
      </div>

      {/* Category breakdown */}
      {Object.keys(data.by_category).length > 0 && (
        <div className="mt-10">
          <div className="overline mb-2">/by category</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.by_category).map(([cat, n]) => (
              <Link
                key={cat}
                to={`/tracker?jurisdiction=${encodeURIComponent(name)}&category=${cat}`}
                className="inline-flex items-center gap-2 border border-black/30 bg-white px-3 py-1.5 hover:bg-black hover:text-white transition"
                data-testid={`cat-${cat}`}
              >
                <span className="font-mono-plex text-xs uppercase tracking-wider">{formatLabel(cat)}</span>
                <span className="font-display font-bold">{n}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Recent entries */}
      <div className="mt-10">
        <div className="overline mb-2">/recent entries</div>
        {data.entries.length === 0 ? (
          <div className="border border-dashed border-black/20 p-8 text-center text-[#555]">No entries yet for this jurisdiction.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.entries.map((e) => <EntryCard key={e.id} entry={e} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function ImpactTile({ label, value, color, testid }) {
  return (
    <div className="bg-white p-5" data-testid={testid}>
      <div className="font-mono-plex text-[11px] uppercase tracking-wider text-[#555]">{label}</div>
      <div className="font-display text-4xl font-black mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
